/**
 * Brera · Banner principal (sections/hero-banner.liquid)
 * - Slideshow: autoplay (data-autoplay en segundos, 0 = no), pausa al pasar el mouse o con el foco,
 *   flechas, indicadores, deslizar con el dedo, transición fundido o deslizar.
 * - Video de fondo: reproduce el video visible (computador o celular), botón de pausa,
 *   respeta "reducir movimiento" del sistema. YouTube y Vimeo se pausan por postMessage.
 * Se vuelve a iniciar cuando el editor recarga la sección y va a la diapositiva elegida en el editor.
 */
(() => {
  if (window.BreraHero) return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Slideshow ---------- */
  const initSlideshow = (hero) => {
    const slides = [...hero.querySelectorAll('[data-slide]')];
    if (slides.length < 2) return null;
    const dots = [...hero.querySelectorAll('[data-slide-dot]')];
    const useSlide = hero.classList.contains('hero--transition-slide') && !reduceMotion;
    const seconds = Number(hero.dataset.autoplay || 0);
    let current = slides.findIndex((s) => s.classList.contains('is-active'));
    if (current < 0) current = 0;
    let timer = null;
    let paused = false;

    const show = (next, dir) => {
      next = (next + slides.length) % slides.length;
      if (next === current) return;
      const from = slides[current];
      const to = slides[next];
      if (dir === undefined) dir = next > current ? 1 : -1;

      if (useSlide) {
        const ms = 700;
        const easing = 'cubic-bezier(0.6, 0, 0.2, 1)';
        from.classList.add('is-leaving');
        from.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-100 * dir}%)` }], { duration: ms, easing });
        to.animate([{ transform: `translateX(${100 * dir}%)` }, { transform: 'translateX(0)' }], { duration: ms, easing });
        setTimeout(() => from.classList.remove('is-leaving'), ms);
      }

      from.classList.remove('is-active');
      from.setAttribute('aria-hidden', 'true');
      to.classList.add('is-active');
      to.removeAttribute('aria-hidden');
      dots.forEach((d, i) => d.setAttribute('aria-current', i === next));
      current = next;
    };

    // Barra de avance (hero--progress): se reinicia en cada diapositiva y se detiene al pausar
    const restartProgress = () => {
      hero.classList.remove('is-playing');
      if (timer) {
        void hero.offsetWidth;
        hero.classList.add('is-playing');
      }
    };
    const stop = () => {
      clearInterval(timer);
      timer = null;
      hero.classList.remove('is-playing');
    };
    const start = () => {
      stop();
      if (seconds > 0 && !paused && !reduceMotion) {
        timer = setInterval(() => { show(current + 1, 1); restartProgress(); }, seconds * 1000);
        restartProgress();
      }
    };

    hero.querySelectorAll('[data-slide-prev]').forEach((b) => b.addEventListener('click', () => { show(current - 1, -1); start(); }));
    hero.querySelectorAll('[data-slide-next]').forEach((b) => b.addEventListener('click', () => { show(current + 1, 1); start(); }));
    dots.forEach((d) => d.addEventListener('click', () => { show(Number(d.dataset.slideDot)); start(); }));

    if (hero.dataset.pauseHover === 'true') {
      hero.addEventListener('mouseenter', () => { paused = true; stop(); });
      hero.addEventListener('mouseleave', () => { paused = false; start(); });
    }
    hero.addEventListener('focusin', () => { paused = true; stop(); });
    hero.addEventListener('focusout', (e) => {
      if (!hero.contains(e.relatedTarget)) { paused = false; start(); }
    });
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

    // Deslizar con el dedo
    let x0 = null;
    let y0 = null;
    hero.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
    hero.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      const dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        show(current + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
        start();
      }
      x0 = null;
    });

    start();
    return { show, stop, start, setPaused: (v) => { paused = v; v ? stop() : start(); }, index: (el) => slides.indexOf(el) };
  };

  /* ---------- Video ---------- */
  const initVideo = (hero) => {
    const toggle = hero.querySelector('[data-video-toggle]');
    const iframe = hero.querySelector('.hero__iframe');
    const visibleVideo = () => [...hero.querySelectorAll('video.hero__video')].find((v) => v.offsetParent !== null || getComputedStyle(v).display !== 'none');
    let playing = false;

    const setState = (isPlaying) => {
      playing = isPlaying;
      if (!toggle) return;
      toggle.classList.toggle('is-paused', !isPlaying);
      toggle.setAttribute('aria-label', isPlaying ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
    };

    const embedCommand = (cmd) => {
      if (!iframe || !iframe.contentWindow) return;
      if (iframe.dataset.embed === 'youtube') {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: cmd === 'play' ? 'playVideo' : 'pauseVideo', args: '' }), '*');
      } else {
        iframe.contentWindow.postMessage(JSON.stringify({ method: cmd }), '*');
      }
    };

    const play = () => {
      const v = visibleVideo();
      if (v) {
        v.muted = true;
        v.preload = 'auto';
        const p = v.play();
        if (p && p.then) p.then(() => setState(true)).catch(() => setState(false));
        else setState(true);
      } else if (iframe) {
        embedCommand('play');
        setState(true);
      }
    };
    const pause = () => {
      hero.querySelectorAll('video.hero__video').forEach((v) => v.pause());
      embedCommand('pause');
      setState(false);
    };

    if (toggle) toggle.addEventListener('click', () => (playing ? pause() : play()));

    if (reduceMotion) {
      if (iframe) iframe.addEventListener('load', () => embedCommand('pause'));
      setState(false);
    } else if (iframe) {
      setState(true);
    } else {
      play();
      // Si cambia de celular a computador (o al revés), reproduce el video que quedó visible
      window.matchMedia('(max-width: 749px)').addEventListener('change', () => {
        if (!playing) return;
        hero.querySelectorAll('video.hero__video').forEach((v) => v.pause());
        play();
      });
    }
    return { play, pause };
  };

  const heroes = new WeakMap();
  const init = (scope) => {
    scope.querySelectorAll('[data-hero]').forEach((hero) => {
      if (heroes.has(hero)) return;
      const api = {};
      if (hero.dataset.mode === 'slideshow') api.slideshow = initSlideshow(hero);
      if (hero.dataset.mode === 'video') api.video = initVideo(hero);
      heroes.set(hero, api);
    });
  };

  const start = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Editor del tema
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest && e.target.closest('[data-slide]');
    if (!slide) return;
    const api = heroes.get(slide.closest('[data-hero]'));
    if (api && api.slideshow) {
      api.slideshow.show(api.slideshow.index(slide));
      api.slideshow.setPaused(true);
    }
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const slide = e.target.closest && e.target.closest('[data-slide]');
    if (!slide) return;
    const api = heroes.get(slide.closest('[data-hero]'));
    if (api && api.slideshow) api.slideshow.setPaused(false);
  });

  window.BreraHero = { init };
})();
